import {allPass} from '@packages/utilities/fp/utils';
import {has} from '@packages/utilities/key-map';
import {cacheMemo, weakMemo} from '@packages/utilities/memo';
import {pseudoMap} from '@packages/systems/core/utils/StyleTypes';
import {getOrderedBreakpointIds} from '@packages/systems/style/media-queries';
import type {BreakpointID} from '@packages/systems/style/types';
import type {PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';

const memoizePseudoKey = cacheMemo(Object.keys(pseudoMap).length);
const memoizeQueries = cacheMemo(getOrderedBreakpointIds().length);
const memoizeVariantStates = cacheMemo(10);

export type PseudoStateData = Partial<Record<PseudoStateKey, Record<any, any>>>;
export type BreakpointData = Partial<Record<BreakpointID, PseudoStateData>>;
export type StyleData = {
  base?: BreakpointData;
  variants?: Record<VariantStateKey, BreakpointData>;
};

const hasPostion = allPass([
  has('gridRowStart'),
  has('gridRowEnd'),
  has('gridColumnStart'),
  has('gridColumnEnd'),
]);

// TODO: Replace this with something more general when we fully implement local styles

/**
 *
 * `createNodeStyleForItem` is used to resolve a node's computed local style given
 * the current media query and pseudo state. Its usage is mainly in the Designer
 * where we may need access to certain style information, such as a grid item's
 * positioning so that we can render the values in the Style Panel.
 */
export const createNodeStyleForItem = memoizeQueries(
  (currentBreakpointId: BreakpointID) =>
    memoizePseudoKey((pseudoMode: PseudoStateKey) =>
      memoizeVariantStates((variantState: VariantStateKey | null) =>
        weakMemo((style: StyleData): Record<any, any> => {
          let breakpointIds = getOrderedBreakpointIds();
          const index = breakpointIds.indexOf(currentBreakpointId);
          breakpointIds = breakpointIds.slice(0, index + 1);

          const result = breakpointIds.reduce<Record<string, any>>(
            (upstreamStyle, breakpointId) => {
              const baseStyle = style.base?.[breakpointId];
              const variantStyle =
                variantState && style.variants?.[variantState]?.[breakpointId];

              if (!baseStyle && !variantStyle) return upstreamStyle;

              /*
               * ----------------------------------------------------------------------------
               * We merge the noPseudoStyles here as well because they cascade sideways to the
               * pseudo states
               * ----------------------------------------------------------------------------
               */
              const currentStyle = {
                // Base styles first
                ...baseStyle?.noPseudo,
                ...baseStyle?.[pseudoMode],
                // Variant styles override base
                ...variantStyle?.noPseudo,
                ...variantStyle?.[pseudoMode],
              } as const;

              return {
                ...upstreamStyle,
                ...currentStyle,
                // Item is manually or auto-placed on a lower MQ, so we ensure that
                // gridArea is set to `null` here — otherwise gridArea takes precedance
                ...(hasPostion(currentStyle) ? {gridArea: null} : null),
              };
            },
            {}
          );

          return result;
        })
      )
    )
);
